import { useState, useEffect, useCallback, useImperativeHandle, forwardRef } from "react";
import axios from "axios";
import { toast } from "@/lib/toast";
import {
  Ticket, MessageSquare, Clock, User, Shield, ChevronLeft, ChevronRight,
  Send, Loader2, Plus, AlertCircle, CheckCircle, XCircle, Pencil, X, Check
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";

const BACKEND_URL = window.BACKEND_URL || process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_CONFIG = {
  open: { label: "Ouvert", color: "bg-amber-500/20 text-amber-400 border-amber-500/30", icon: AlertCircle },
  answered: { label: "Répondu", color: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30", icon: CheckCircle },
  closed: { label: "Fermé", color: "bg-zinc-500/20 text-zinc-400 border-zinc-500/30", icon: XCircle },
};

function formatRelative(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return "À l'instant";
  if (diffMins < 60) return `${diffMins}min`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}j`;
  return new Date(dateString).toLocaleDateString("fr-FR", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit"
  });
}

function UserTicketsSection({ token }, ref) {
  const [tickets, setTickets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketDetail, setTicketDetail] = useState(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [replyMessage, setReplyMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editContent, setEditContent] = useState("");

  // Create form
  const [showCreate, setShowCreate] = useState(false);
  const [newSubject, setNewSubject] = useState("");
  const [newMessage, setNewMessage] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchTickets = useCallback(async (silent = false) => {
    if (!silent) setIsLoading(true);
    try {
      const res = await axios.get(`${API}/user/tickets`, { headers: { Authorization: `Bearer ${token}` } });
      setTickets(res.data);
    } catch { /* silent */ }
    finally { if (!silent) setIsLoading(false); }
  }, [token]);

  const fetchTicketDetail = useCallback(async (ticketId, silent = false) => {
    if (!silent) setIsLoadingDetail(true);
    try {
      const res = await axios.get(`${API}/user/tickets/${ticketId}`, { headers: { Authorization: `Bearer ${token}` } });
      setTicketDetail(res.data);
    } catch {
      if (!silent) toast.error("Erreur lors du chargement");
    } finally { if (!silent) setIsLoadingDetail(false); }
  }, [token]);

  useImperativeHandle(ref, () => ({
    refresh: () => {
      fetchTickets();
      if (selectedTicket) fetchTicketDetail(selectedTicket.id);
    }
  }), [fetchTickets, fetchTicketDetail, selectedTicket]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  // Auto-refresh tickets every 30s
  useEffect(() => {
    const interval = setInterval(() => {
      fetchTickets(true);
      if (selectedTicket) fetchTicketDetail(selectedTicket.id, true);
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchTickets, fetchTicketDetail, selectedTicket]);

  const openTicket = (ticket) => {
    setSelectedTicket(ticket);
    setReplyMessage("");
    fetchTicketDetail(ticket.id);
  };

  const createTicket = async () => {
    if (!newSubject.trim() || !newMessage.trim()) return;
    setIsCreating(true);
    try {
      await axios.post(`${API}/user/tickets`, {
        subject: newSubject.trim(),
        message: newMessage.trim(),
      }, { headers });
      toast.success("Ticket créé");
      setNewSubject("");
      setNewMessage("");
      setShowCreate(false);
      fetchTickets();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la création");
    } finally { setIsCreating(false); }
  };

  const handleReply = async () => {
    if (!replyMessage.trim() || !selectedTicket) return;
    setIsSending(true);
    try {
      await axios.post(`${API}/user/tickets/${selectedTicket.id}/reply`, { message: replyMessage }, { headers });
      toast.success("Message envoyé");
      setReplyMessage("");
      fetchTicketDetail(selectedTicket.id);
      fetchTickets();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setIsSending(false); }
  };

  const handleEditMessage = async (ticketId, messageId) => {
    if (!editContent.trim()) return;
    try {
      await axios.patch(`${API}/user/tickets/${ticketId}/messages/${messageId}`, { content: editContent }, { headers });
      setEditingMessageId(null);
      setEditContent("");
      fetchTicketDetail(ticketId, true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  // Detail view
  if (selectedTicket) {
    const sc = STATUS_CONFIG[ticketDetail?.status || selectedTicket.status] || STATUS_CONFIG.open;
    return (
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center gap-3">
          <button onClick={() => { setSelectedTicket(null); setTicketDetail(null); }} className="text-zinc-400 hover:text-white p-1 rounded-lg hover:bg-zinc-800">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h2 className="text-white font-medium truncate text-sm">{selectedTicket.subject}</h2>
            <div className="flex items-center gap-2 mt-0.5">
              <Badge className={`${sc.color} text-[10px]`}>{sc.label}</Badge>
              <span className="text-[10px] text-zinc-600">{formatRelative(selectedTicket.created_at)}</span>
            </div>
          </div>
        </div>

        {/* Messages */}
        {isLoadingDetail ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-5 h-5 text-zinc-500 animate-spin" />
          </div>
        ) : (
          <ScrollArea className="h-[calc(100vh-420px)] min-h-[250px]">
            <div className="space-y-3 pr-2">
              {ticketDetail?.messages?.map((msg) => {
                const ticketOwner = ticketDetail?.username || selectedTicket.username;
                const isMine = msg.author === ticketOwner;
                const isAdmin = !isMine;
                const isEditing = editingMessageId === msg.id;
                return (
                  <div key={msg.id} className={`flex ${isAdmin ? "justify-start" : "justify-end"}`}>
                    <div className={`group/msg relative max-w-[85%] rounded-xl p-3.5 ${
                      isAdmin
                        ? "bg-amber-500/10 border border-amber-500/20 rounded-tl-sm"
                        : "bg-cyan-500/10 border border-cyan-500/20 rounded-tr-sm"
                    }`}>
                      {/* Edit button on hover (own messages only) */}
                      {isMine && !isEditing && (
                        <div className="absolute -left-10 top-1 opacity-0 group-hover/msg:opacity-100 transition-opacity duration-200">
                          <button
                            onClick={() => { setEditingMessageId(msg.id); setEditContent(msg.content); }}
                            className="p-1.5 rounded-md text-zinc-500 hover:text-cyan-400 hover:bg-zinc-800/50 transition-colors group/edit"
                            title="Modifier"
                          >
                            <Pencil className="w-3.5 h-3.5 group-hover/edit:animate-[wiggle_0.4s_ease-in-out]" />
                          </button>
                        </div>
                      )}
                      <div className="flex items-center gap-2 mb-1.5">
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center ${isAdmin ? "bg-amber-500/20" : "bg-cyan-500/20"}`}>
                          {isAdmin ? <Shield className="w-3 h-3 text-amber-400" /> : <User className="w-3 h-3 text-cyan-400" />}
                        </div>
                        <span className={`text-[11px] font-medium ${isAdmin ? "text-amber-400" : "text-cyan-400"}`}>
                          {isAdmin ? "Support ShadowMail" : "Moi"}
                        </span>
                        <span className="text-[10px] text-zinc-600">{formatRelative(msg.created_at)}</span>
                        {msg.edited_at && <span className="text-[10px] text-zinc-600 italic">modifié</span>}
                      </div>
                      {isEditing ? (
                        <div className="space-y-2">
                          <textarea
                            value={editContent}
                            onChange={(e) => setEditContent(e.target.value)}
                            className="w-full bg-zinc-900/80 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-200 resize-none focus:outline-none focus:border-cyan-500/50"
                            rows={3}
                            autoFocus
                          />
                          <div className="flex items-center gap-1.5 justify-end">
                            <button onClick={() => { setEditingMessageId(null); setEditContent(""); }} className="p-1.5 rounded-md text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800 transition-colors">
                              <X className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={() => handleEditMessage(selectedTicket.id, msg.id)} className="p-1.5 rounded-md text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10 transition-colors">
                              <Check className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-zinc-300 whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        )}

        {/* Reply */}
        {(ticketDetail?.status || selectedTicket.status) !== "closed" && (
          <div className="flex gap-2 pt-2 border-t border-zinc-800">
            <Input
              value={replyMessage}
              onChange={(e) => setReplyMessage(e.target.value)}
              placeholder="Écrire un message..."
              className="bg-zinc-900 border-zinc-800 text-white text-sm"
              onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && handleReply()}
            />
            <Button
              onClick={handleReply}
              disabled={isSending || !replyMessage.trim()}
              className="bg-cyan-500 hover:bg-cyan-600 text-black shrink-0"
              size="sm"
            >
              {isSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
        )}

        {(ticketDetail?.status || selectedTicket.status) === "closed" && (
          <div className="text-center py-3 text-zinc-600 text-xs border-t border-zinc-800">
            Ce ticket est fermé
          </div>
        )}
      </div>
    );
  }

  // Create form view
  if (showCreate) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <button onClick={() => setShowCreate(false)} className="text-zinc-400 hover:text-white p-1 rounded-lg hover:bg-zinc-800 transition-colors">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-cyan-500/15 border border-cyan-500/25 flex items-center justify-center">
              <Ticket className="w-4 h-4 text-cyan-400" />
            </div>
            <h2 className="text-white font-medium text-sm">Nouveau ticket</h2>
          </div>
        </div>

        <div className="relative bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-cyan-500/50 to-transparent" />
          <div className="p-5 space-y-5">
            <div>
              <label className="text-[10px] text-cyan-400/60 uppercase tracking-wider font-medium mb-2 block">Sujet</label>
              <Input
                value={newSubject}
                onChange={(e) => setNewSubject(e.target.value)}
                placeholder="Décrivez brièvement votre demande"
                className="bg-zinc-800/50 border-zinc-700/50 text-white text-sm focus:border-cyan-500/50 transition-colors"
                autoFocus
              />
            </div>

            <div>
              <label className="text-[10px] text-cyan-400/60 uppercase tracking-wider font-medium mb-2 block">Message</label>
              <textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Détaillez votre demande..."
                rows={5}
                className="w-full bg-zinc-800/50 border border-zinc-700/50 text-white text-sm rounded-lg px-3 py-2.5 resize-none focus:outline-none focus:border-cyan-500/50 placeholder:text-zinc-600 transition-colors"
              />
            </div>

            <Button
              onClick={createTicket}
              disabled={isCreating || !newSubject.trim() || !newMessage.trim()}
              className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-black text-sm font-semibold py-2.5 rounded-lg transition-colors shadow-lg shadow-cyan-500/20 hover:shadow-cyan-500/30"
            >
              {isCreating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Envoyer
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // List view
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-white font-medium text-sm flex items-center gap-2">
          <Ticket className="w-4 h-4 text-cyan-400" />
          Mes tickets
        </h2>
        <Button
          onClick={() => setShowCreate(true)}
          size="sm"
          className="bg-cyan-500 hover:bg-cyan-600 text-black text-xs"
        >
          <Plus className="w-3.5 h-3.5 mr-1" />
          Nouveau
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-5 h-5 text-zinc-500 animate-spin" />
        </div>
      ) : tickets.length === 0 ? (
        <div className="text-center py-16 space-y-3">
          <div className="w-14 h-14 rounded-full bg-zinc-900 flex items-center justify-center mx-auto border border-zinc-800">
            <Ticket className="w-7 h-7 text-zinc-700" />
          </div>
          <p className="text-zinc-500 text-sm">Aucun ticket</p>
          <p className="text-zinc-600 text-xs">Créez un ticket pour contacter le support</p>
        </div>
      ) : (
        <div className="space-y-2">
          {tickets.map((ticket) => {
            const sc = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.open;
            const StatusIcon = sc.icon;
            return (
              <button
                key={ticket.id}
                onClick={() => openTicket(ticket)}
                className="w-full text-left bg-zinc-900 border border-zinc-800 rounded-xl p-3.5 hover:border-cyan-500/30 transition-colors group"
              >
                <div className="flex items-start gap-3">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
                    ticket.status === "open" ? "bg-amber-500/15 border border-amber-500/20" :
                    ticket.status === "answered" ? "bg-emerald-500/15 border border-emerald-500/20" :
                    "bg-zinc-800 border border-zinc-700"
                  }`}>
                    <StatusIcon className={`w-4 h-4 ${
                      ticket.status === "open" ? "text-amber-400" :
                      ticket.status === "answered" ? "text-emerald-400" :
                      "text-zinc-500"
                    }`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm text-white font-medium truncate">{ticket.subject}</p>
                      <Badge className={`${sc.color} text-[9px] px-1.5 shrink-0`}>{sc.label}</Badge>
                      {ticket.priority === "high" && (
                        <Badge className="bg-red-500/20 text-red-400 border-red-500/30 text-[9px] px-1.5">Urgent</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-[11px] text-zinc-500 flex items-center gap-1">
                        <MessageSquare className="w-3 h-3" /> {ticket.message_count} msg
                      </span>
                      <span className="text-[11px] text-zinc-600 flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {formatRelative(ticket.last_message_at || ticket.updated_at)}
                      </span>
                      {ticket.read_by_user === false && (
                        <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-[9px] px-1 flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                          Nouvelle réponse
                        </Badge>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-zinc-700 group-hover:text-cyan-400 transition-colors shrink-0 mt-1" />
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default forwardRef(UserTicketsSection);
